"use client";
import {
  MyAvailableAssets,
  ModalQr,
  UAccountGeneralData,
  useModalQr,
} from "components/account";
import { useCustomWallet } from "components/providers";
import { WalletType } from "contracts/types";
import { handleErrors } from "errors";
import unlockdWalletModule from "logic/UnlockdWalletModule";
import { useErrorAlert, useNftSelectionV3 } from "logic/hooks";
import { useGetNfts } from "logic/hooks/borrow";
import { INft } from "logic/types/nft/INft";
import { useEffect, useMemo, useState } from "react";

export default function Account(): JSX.Element {
  const { smartAddress } = useCustomWallet();
  const [error, setError] = useState<Error | null>(null);
  useErrorAlert(error);

  const getNftsOptions = useMemo(
    () => ({
      activeFilters: {
        assetOrigin: false,
        categories: true,
        collection: true,
        currency: false,
        order: false,
      },
      fixedValues: { assetOrigin: WalletType.SMART },
    }),
    []
  );

  const {
    nfts,
    filterOptions,
    handleRemoveNfts,
    isLoading: isLoadingNfts,
    areNfts,
  } = useGetNfts(getNftsOptions);

  const totalAssets = useMemo<number | undefined>(
    () => (isLoadingNfts ? undefined : nfts?.length),
    [nfts, isLoadingNfts]
  );

  const { selectedNfts, toggleSelectedNft } = useNftSelectionV3(nfts, {});
  const [ethBalance, setEthBalance] = useState<bigint>();
  const [isModalQrOpen, toggleModalQr] = useModalQr();

  useEffect(() => {
    if (smartAddress) {
      handleGetBalance();

      return () => {
        setEthBalance(undefined);
      };
    }
  }, [smartAddress]);

  const handleNftsWithdrawn = (nfts: INft[]) => {
    handleRemoveNfts(nfts);
  };

  const handleGetBalance = async () => {
    try {
      setEthBalance(await unlockdWalletModule.getNativeBalance());
    } catch (err) {
      setError(handleErrors(err));
    }
  };

  return (
    <>
      <UAccountGeneralData
        className="w-[1237px] mx-auto"
        totalAssets={totalAssets}
        ethBalance={ethBalance}
      />
      <MyAvailableAssets
        className="mt-[30px] w-[1237px] mx-auto"
        nfts={nfts}
        isLoadingNfts={isLoadingNfts}
        onNftsWithdrawn={handleNftsWithdrawn}
        selectedNfts={selectedNfts}
        onSelectedNft={toggleSelectedNft}
        filterOptions={filterOptions}
        areNfts={areNfts}
      />
      {isModalQrOpen && smartAddress && (
        <ModalQr isOpen={isModalQrOpen} toggleModal={toggleModalQr} />
      )}
    </>
  );
}
